/**
 * The calendar's campaign chips AFTER they went out — "the same calendar, only marked sent".
 *
 * The product owner: "show everything the way it was shown when it was due to be sent,
 * just marked as sent … the same calendar, the same number of events, only sent."
 * Before a campaign goes out the calendar draws ONE chip for it (a standalone campaign, or
 * one step of a sequence) at its send time. So after it goes out it draws that same chip,
 * at that same time, marked — never one chip per recipient. Pure functions, so the
 * boundary between the upcoming pass and this one can be tested without a store.
 *
 * WHERE THE DOCUMENTS COME FROM
 *   - `DBCampaignReducer` — the live copies. A step that finishes while the page is open is
 *     patched there (pending → running → complete-*), so it moves from the upcoming pass to
 *     this one on the same render, with no gap in between.
 *   - `PlannerSentHistoryReducer` — completed campaigns fetched for the visible range while
 *     "Show sent" is on.
 * The same campaign can be in both; the newer copy wins (`mergeCampaignDocs`), so a stale
 * pending copy can never draw next to its own completed chip.
 */
import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import type { Campaign } from '@common/models/campaign-model';
import {
    campaignChipVisible,
    campaignOutcomeOf,
    campaignRecipientSplit,
    messageMatchesPlannerFilters,
    plannerOutcomeOf,
    type CampaignRecipientSplit,
    type PlannerFilters,
} from '@common/services/planner-filters';

type CampaignDoc = Campaign & { _id?: unknown; version?: number; updatedAt?: unknown; deleted?: boolean };

const versionOf = (c: CampaignDoc) => (typeof c.version === 'number' ? c.version : 0);
const updatedMs = (c: CampaignDoc) => {
    const ms = c.updatedAt ? new Date(c.updatedAt as any).getTime() : NaN;
    return Number.isNaN(ms) ? 0 : ms;
};

/** One copy per campaign id — the newer one (by version, then by updatedAt). */
export function mergeCampaignDocs(...sources: ReadonlyArray<ReadonlyArray<CampaignDoc | undefined>>): CampaignDoc[] {
    const byId = new Map<string, CampaignDoc>();
    for (const list of sources) {
        for (const c of list) {
            if (!c?._id) continue;
            const id = String(c._id);
            const have = byId.get(id);
            if (!have || versionOf(c) > versionOf(have) || (versionOf(c) === versionOf(have) && updatedMs(c) > updatedMs(have))) {
                byId.set(id, c);
            }
        }
    }
    return [...byId.values()];
}

/** What a campaign's failed sends add up to — the "didn't go out" half of its chip. */
export interface CampaignFailureFold {
    count: number;
    /** The earliest failed send — where the chip goes when the campaign doc is not loaded. */
    firstDueMs: number;
    /** One failed send, for the chip's title and picture when the campaign doc is absent. */
    sample: UserMessage;
    /** All of them — so the chip answers the filters exactly as the list of them does. */
    messages: UserMessage[];
}

/**
 * Does the sidebar LIST show any of this campaign's failed sends right now? Then the
 * calendar shows its chip — one surface names the sends, the other draws the one chip they
 * belong to, and neither can show something the other hides ("Groups only" included: a
 * failed send to a group is listed, so its campaign's chip is drawn).
 */
export function foldListedUnder(fold: CampaignFailureFold | undefined, filters: PlannerFilters): boolean {
    return !!fold && fold.messages.some(m => messageMatchesPlannerFilters(m, filters));
}

/**
 * Group the store's failed campaign sends by campaign. Failed sends stay live rows and are
 * loaded eagerly, so this costs no fetch. Counted whatever the filters say — the count
 * describes the chip, and a chip must not claim "all sent" because "Show failed" is off.
 */
export function foldCampaignFailures(messages: ReadonlyArray<UserMessage>): Map<string, CampaignFailureFold> {
    const out = new Map<string, CampaignFailureFold>();
    for (const m of messages) {
        const campaignId = (m as { campaignId?: unknown })?.campaignId;
        if (!campaignId || m.deleted === true) continue;
        if (plannerOutcomeOf(m) !== 'failed') continue;
        const id = String(campaignId);
        const due = m.dueDate ? new Date(m.dueDate as any).getTime() : NaN;
        const dueMs = Number.isNaN(due) ? Number.POSITIVE_INFINITY : due;
        const have = out.get(id);
        if (!have) out.set(id, { count: 1, firstDueMs: dueMs, sample: m, messages: [m] });
        else {
            have.count += 1;
            have.messages.push(m);
            if (dueMs < have.firstDueMs) { have.firstDueMs = dueMs; have.sample = m; }
        }
    }
    return out;
}

export interface CampaignChipOutcome {
    /** The status the chip is drawn with — read by the same status → outcome map as a message. */
    effectiveStatus?: 'confirmed' | 'failed';
    /** "2/3 sent": set for a campaign that went out, when its document says how many it queued. */
    recipientSplit?: CampaignRecipientSplit;
    /** Failures known without a denominator (a campaign still running, or one whose doc is absent). */
    failedCount?: number;
}

/** How a campaign chip is marked: fully sent → sent; anything that did not go out → the failure mark. */
export function campaignChipOutcome(doc: CampaignDoc | undefined, fold: CampaignFailureFold | undefined): CampaignChipOutcome {
    const failed = fold?.count ?? 0;
    const outcome = campaignOutcomeOf(doc?.campaignStatus);
    if (outcome === 'sent') {
        const split = campaignRecipientSplit(doc as any, failed);
        if (split) return { effectiveStatus: split.notSent > 0 ? 'failed' : 'confirmed', recipientSplit: split };
        return failed > 0 ? { effectiveStatus: 'failed', failedCount: failed } : { effectiveStatus: 'confirmed' };
    }
    // Still upcoming (or running): the chip keeps its upcoming look, but a failure so far is
    // already a fact the reader must see — the one chip is the only place it can go.
    return failed > 0 ? { effectiveStatus: 'failed', failedCount: failed } : {};
}

/**
 * Where a sequence's CURRENT step sends, read off the last step that already went out.
 *
 * Between "step N completed" and "step N+1's campaign document exists" the drip has no
 * upcoming step campaign in the store, so its remaining steps are projected from the drip
 * document alone — which anchors on the drip's START, i.e. on step 0. Walking the gaps from
 * there puts step N+1 at step 0's time plus one gap: in the past, so it was dropped, and
 * the next step vanished exactly at the moment the one before it finished. The completed
 * step knows when it really went out; walking the gaps from it puts every later step where
 * the upcoming pass drew it before.
 */
export function currentStepDueFromCompleted(
    drip: { currentStep?: number; steps?: ReadonlyArray<{ stepIndex: number; gapAfter?: any }> } | undefined,
    completed: ReadonlyArray<{ stepIndex: number; dueMs: number }> | undefined,
    project: (baseAt: Date, steps: ReadonlyArray<{ stepIndex: number; gapAfter?: any }>, fromIndex: number) => Array<{ stepIndex: number; at: Date }>,
): Date | undefined {
    if (!drip?.steps?.length || !completed?.length) return undefined;
    const latest = completed.reduce((a, b) => (b.stepIndex > a.stepIndex || (b.stepIndex === a.stepIndex && b.dueMs > a.dueMs) ? b : a));
    const currentIndex = typeof drip.currentStep === 'number' ? drip.currentStep : 0;
    if (currentIndex < latest.stepIndex) return undefined;
    const fromLatest = project(new Date(latest.dueMs), drip.steps, latest.stepIndex);
    return fromLatest.find(p => p.stepIndex === currentIndex)?.at;
}

const inRange = (ms: number, rangeStart: Date, rangeEnd: Date) =>
    !Number.isNaN(ms) && ms >= rangeStart.getTime() && ms <= rangeEnd.getTime();

/**
 * The sent pass: one chip per campaign that went out, at its own send time. A completed
 * campaign is never re-expanded — its recurrence (if it had one) already produced the next
 * occurrence as a document of its own, and its own `schedule.dueDate` IS the one send.
 */
export function buildSentCampaignEvents(
    docs: ReadonlyArray<CampaignDoc>,
    opts: { filters: PlannerFilters; rangeStart: Date; rangeEnd: Date; failures: Map<string, CampaignFailureFold>; color: string },
): any[] {
    const events: any[] = [];
    for (const c of docs) {
        if (c.deleted === true || campaignOutcomeOf(c.campaignStatus) !== 'sent') continue;
        const due = c.schedule?.dueDate;
        const dueMs = due ? new Date(due as any).getTime() : NaN;
        if (!inRange(dueMs, opts.rangeStart, opts.rangeEnd)) continue;
        const fold = opts.failures.get(String(c._id));
        const marks = campaignChipOutcome(c, fold);
        const split = marks.recipientSplit ?? (marks.failedCount ? { sent: 1, notSent: marks.failedCount } : null);
        if (!campaignChipVisible(opts.filters, 'sent', split) && !foldListedUnder(fold, opts.filters)) continue;
        events.push({
            ...c,
            dueDate: due,
            isRecurring: !!c.schedule?.isRecurring,
            messageColor: opts.color,
            ...marks,
        });
    }
    return events;
}

/**
 * A campaign whose failures are in the store but whose document is not (it completed
 * before this session and "Show sent" has not loaded it): its failures still belong on
 * ONE chip, where the campaign sent, not on one chip per recipient. Placed at the earliest
 * failure, titled from it, marked failed with the count.
 */
export function buildFailedCampaignGroupEvents(
    failures: Map<string, CampaignFailureFold>,
    knownCampaignIds: ReadonlySet<string>,
    opts: { filters: PlannerFilters; rangeStart: Date; rangeEnd: Date; color: string },
): any[] {
    const events: any[] = [];
    for (const [campaignId, fold] of failures) {
        if (knownCampaignIds.has(campaignId)) continue;
        if (!inRange(fold.firstDueMs, opts.rangeStart, opts.rangeEnd)) continue;
        if (!foldListedUnder(fold, opts.filters)) continue;
        const at = new Date(fold.firstDueMs);
        events.push({
            _id: campaignId,
            schedule: { dueDate: at },
            dueDate: at,
            isRecurring: false,
            template: { message: typeof fold.sample.message === 'string' ? fold.sample.message : '', asset: (fold.sample as any).asset },
            messageColor: opts.color,
            effectiveStatus: 'failed',
            failedCount: fold.count,
            isFailedCampaignGroup: true,
        });
    }
    return events;
}
